import Vue from 'vue'
import findIndex from 'lodash/findIndex'
import { AppHolder } from '@/utils/app-holder'

// API resources
import apiInvitations from '@/api/modules/invitations'
import customers from '@/api/modules/customers'

// Mutations
import {
  RESET_MODULE
} from '@/store/mutation-types'

// Default state
const getDefaultState = () => {
  return {
    aggregation: {},
    list: [],
    extraCurrentCustomer: null,
    meta: {},
    stats: {},
    filters: {},
    events: [],
    invitedList: [],
    invitedMeta: {}
  }
}
const state = getDefaultState()

// -----------------------------------------------------------------------------

const actions = {
  fetch (context, params = { filters: {}, page: undefined }) {
    return customers.fetch(params.filters, params.page)
      .then(data => {
        context.commit('updateCustomersList', data)
        context.commit('setFilters', params.filters)
      })
  },

  fetchById ({ state, commit }, { id, cached = true }) {
    if (cached && (state.list.find(customer => customer.id === id) ||
      (state.extraCurrentCustomer && state.extraCurrentCustomer.id === id))) {
      return
    }
    return customers.fetchById(id)
      .then(data => commit('fetchById', data))
  },

  fetchAccounts (context, {id, cached = true}) {
    return customers.fetchAccounts(id)
      .then(data => {
        AppHolder.store.commit('accounts/fetch', data)

        return Promise.all([
          customers.fetchComposition(id, 'bank', 1)
            .then(({ data }) => AppHolder.store.commit('accounts/fetchRepartition', data)),
          customers.fetchComposition(id, 'kind')
            .then(({ data }) => {
              AppHolder.store.commit('contracts/fetchRepartition', data)
            })
        ])
      })
  },

  fetchEvents ({ commit }, id) {
    return customers.fetchEvents(id)
      .then(({ events }) => commit('setEvents', events))
  },

  fetchUser (context, {id, cached = true}) {
    return customers.fetchUser(id)
      .then(data => customers.fetchProfile(id)
        .then(profile => {
          AppHolder.store.commit('user/fetch', Object.assign(data, profile.profile))
        })
      )
  },

  fetchPortfolios (context, {id, cached = true}) {
    return customers.fetchPortfolios(id)
      .then(data => {
        AppHolder.store.commit('contracts/fetch', data)

        return customers.fetchComposition(id, 'contract')
          .then(({ data }) => {
            AppHolder.store.commit('contracts/fetchRepartition', data)
          })
      })
  },

  fetchStats ({ commit }) {
    return customers.fetchStats()
      .then(data => {
        commit('setStats', data)
      })
  },

  fetchInvited (context, params = { filters: {}, page: undefined }) {
    return customers.fetchInvited(params.filters, params.page)
      .then(data => {
        context.commit('fetchInvited', data)
      })
  },

  createInvitation (context, invitation) {
    return apiInvitations.createInvitation(invitation)
  },

  resendInvitation (context, invitationId) {
    return apiInvitations.resendInvitation(invitationId)
  },

  cancelInvitation (context, invitationId) {
    return apiInvitations.cancelInvitation(invitationId)
      .then(() => context.dispatch('fetchInvited'))
  },

  addEvent ({ commit }, { id, event }) {
    return customers.addEvent(id, event)
      .then(() => customers.fetchEvents(id)
        .then(({ events }) => commit('setEvents', events)))
  },

  deleteEvent (context, { id }) {
    return customers.deleteEvent(id)
  },

  editEvent (context, { id, event }) {
    return customers.editEvent(id, event)
  },

  searchCustomers ({ commit }, { filters, size }) {
    return customers.searchCustomers({ filters, size })
      .then(data => {
        commit('updateCustomersList', data)
        return data
      })
      .catch(response => {
        return Promise.reject(response)
      })
  },

  resetState (context) {
    context.commit(RESET_MODULE)
  }
}

const mutations = {
  updateCustomersList (state, data) {
    if (data.customers) {
      Vue.set(state, 'list', data.customers)
    }
    if (data.meta) {
      Vue.set(state, 'meta', data.meta)
      state.meta = data.meta
    }
    if (data.aggregation) {
      Vue.set(state, 'aggregation', data.aggregation)
    }
  },

  fetchInvited (state, data) {
    state.invitedList = data.invitations || []
    state.invitedMeta = data.meta || {}
  },

  setFilters (state, filters) {
    state.filters = filters
  },

  fetchById (state, data) {
    state.extraCurrentCustomer = data
  },

  setStats (state, stats) {
    state.stats = stats
  },

  setEvents (state, events) {
    state.events = events
  },

  setNotes (state, { customerId, data }) {
    const index = findIndex(state.list, { id: customerId })
    if (state.extraCurrentCustomer && state.extraCurrentCustomer.id === customerId) {
      state.extraCurrentCustomer = {
        ...state.extraCurrentCustomer,
        advisorNotes: data
      }
    }
    if (index >= 0) {
      state.list = [
        ...state.list.slice(0, index),
        {
          ...state.list[index],
          advisorNotes: data
        },
        ...state.list.slice(index + 1)
      ]
    }
  },

  [RESET_MODULE] (state) {
    Object.assign(state, getDefaultState())
  }
}

const getters = {
  getById: ({ list, extraCurrentCustomer }) => (id) => {
    return extraCurrentCustomer && extraCurrentCustomer.id === id
      ? extraCurrentCustomer
      : list.find(customer => customer.id === id)
  }
}

export default () => ({
  namespaced: true,
  state,
  actions,
  mutations,
  getters
})
